import { type UIFacet, type UIFacetOption } from 'src/components/types/UIFacet';
import { type ReduxApplicationState } from 'src/redux/combinedReducer';
import { type Facet, type FacetOption } from 'src/redux/productsQuery/types/Facet';
import { buildListingsURLFromState } from './state/buildListingsURLFromState';

type StoreFacetOption = Facet['options'][number];

export type FacetOptionWithResetHref = Omit<StoreFacetOption, 'href'> & {
  href?: string;
};

export type FacetWithResetHrefs = Omit<Facet, 'options'> & {
  options: FacetOptionWithResetHref[];
};

/**
 * The store option for `alias`/`value`, when it exists (optionally constrained to
 * a facet `type`). Returned so callers can both confirm the option is real and
 * read its current `active`/`count` state.
 */
const findStoreOption = (
  facets: Facet[],
  alias: string,
  value: string,
  type?: Facet['type'],
): FacetOption | undefined => {
  const facet = facets.find((candidate) => candidate.alias === alias);
  if (!facet || (type && facet.type !== type)) {
    return undefined;
  }
  return facet.options.find((option) => option.value === value);
};

/** A zero-count option that is not already selected has nothing to link to. */
const isLinkable = (option: FacetOption): boolean => !(option.count === 0 && !option.active);

/**
 * The href representing the page after toggling `alias`/`value`: an active option
 * links to the page with it removed, an inactive one to the page with it added.
 * Returns `undefined` for options that are not linkable (missing or disabled).
 */
const deriveFacetOptionToggleHref = (
  state: ReduxApplicationState,
  alias: string,
  value: string,
  type?: Facet['type'],
): string | undefined => {
  const storeOption = findStoreOption(state.productsQuery.facets, alias, value, type);

  if (!storeOption || !isLinkable(storeOption)) {
    return undefined;
  }

  return buildListingsURLFromState(state, {
    filter: storeOption.active ? { remove: { alias, value } } : { add: { alias, value } },
    page: 1,
  });
};

export const adaptStoreToActiveFacetResetHrefs = (
  state: ReduxApplicationState,
  activeFacets: Facet[],
): FacetWithResetHrefs[] => {
  return activeFacets.map((facet) => ({
    ...facet,
    options: facet.options.map((option) => ({
      ...option,
      href: option.active
        ? deriveFacetOptionToggleHref(state, facet.alias, option.value)
        : undefined,
    })),
  }));
};

export const getClearAllFacetsHref = (state: ReduxApplicationState): string =>
  // Clearing keeps the taxonomy path (the route + any active taxonomy levels) and
  // drops only the `/-/` filter selections.
  buildListingsURLFromState(state, { filter: { clear: true }, page: 1 });

export const adaptStoreToUIFacetHrefs = (
  state: ReduxApplicationState,
  uiFacets: UIFacet[],
): UIFacet[] => {
  return uiFacets.map((facet) => ({
    ...facet,
    options: facet.options.map((option: UIFacetOption) => ({
      ...option,
      href: deriveFacetOptionToggleHref(state, facet.alias, option.value, facet.type),
    })),
  }));
};
